import { useEffect, useState } from "react";
import * as anagraficaApi from "../../api/anagrafica";
import { IstitutoVigilanza } from "../../api/anagrafica";
import { useAuth } from "../../context/AuthContext";
import { Input } from "./Input";

/**
 * Valore della tendina che significa "l'istituto non è fra quelli censiti":
 * non è un id, è una modalità. Sta qui e non nei due form perché è la stessa
 * convenzione da entrambe le parti — e perché `payloadVigilanza` qui sotto la
 * traduce in colonne una volta sola.
 */
export const ALTRO = "__altro__";

/**
 * Dalle due scelte dell'interfaccia alle due colonne del database.
 *
 * È la faccia frontend di `vigilanzaCongiunta` in routes/interventions.ts, e
 * dice la stessa cosa: se l'intervento non è congiunto non c'è nessun
 * istituto; chiave e testo non convivono mai. Il backend la riapplica comunque
 * — è lui il controllo vero, qui si evita solo di mandare richieste che sa già
 * di dover ripulire. Le due copie vanno tenute identiche alla lettera.
 */
export function payloadVigilanza(
  richiede: boolean,
  scelta: string,
  altro: string
): { richiede_vigilanza: boolean; istituto_vigilanza_congiunto_id: string | null; istituto_vigilanza_congiunto_nome: string | null } {
  if (!richiede) {
    return { richiede_vigilanza: false, istituto_vigilanza_congiunto_id: null, istituto_vigilanza_congiunto_nome: null };
  }
  if (scelta === ALTRO) {
    return {
      richiede_vigilanza: true,
      istituto_vigilanza_congiunto_id: null,
      istituto_vigilanza_congiunto_nome: altro.trim() || null,
    };
  }
  return {
    richiede_vigilanza: true,
    istituto_vigilanza_congiunto_id: scelta || null,
    istituto_vigilanza_congiunto_nome: null,
  };
}

/** Dai due campi del record al valore della tendina — l'inverso di sopra. */
export function sceltaDaRecord(istitutoId: string | null, nomeAltro: string | null): string {
  if (istitutoId) return istitutoId;
  if (nomeAltro) return ALTRO;
  return "";
}

interface Props {
  /** "Da effettuare congiuntamente alla vigilanza". */
  richiede: boolean;
  onRichiedeChange: (v: boolean) => void;
  /** "" | uuid dell'istituto | ALTRO. */
  scelta: string;
  onSceltaChange: (v: string) => void;
  /** Nome scritto a mano, usato solo quando `scelta` è ALTRO. */
  altro: string;
  onAltroChange: (v: string) => void;
  /**
   * Il nome già risolto dal backend (`congiunto_con`), per chi non ha il
   * permesso di leggere l'anagrafica degli istituti e quindi non può avere la
   * tendina: senza, vedrebbe un UUID o niente.
   */
  congiuntoCon?: string | null;
  disabled?: boolean;
}

/**
 * "Con chi si va sul posto": la spunta del congiunto e, quando è accesa, QUALE
 * istituto — dall'elenco dei censiti oppure "Altro" scritto a mano.
 *
 * Un componente e non due blocchi copiati, perché lo usano sia il form di
 * creazione sia quello di modifica: è dal secondo che si taggano gli
 * interventi congiunti già esistenti, e due copie della stessa regola
 * divergerebbero al primo cambiamento.
 *
 * ## Perché la tendina può non esserci
 *
 * L'elenco degli istituti sta dietro `anagrafica:read`, che un OPERATORE
 * TECNICO non ha — e un operatore tecnico gli interventi può modificarli
 * (`interventi:update`). Per lui il campo diventa di sola lettura e mostra il
 * nome che arriva già risolto dal backend: meglio di una tendina vuota, che
 * sembrerebbe "nessun istituto censito" invece di "non puoi sceglierlo tu".
 */
export function VigilanzaCongiunta({
  richiede,
  onRichiedeChange,
  scelta,
  onSceltaChange,
  altro,
  onAltroChange,
  congiuntoCon,
  disabled,
}: Props) {
  const { hasPermission } = useAuth();
  const puoScegliere = hasPermission("anagrafica:read");
  const [istituti, setIstituti] = useState<IstitutoVigilanza[]>([]);

  useEffect(() => {
    // Si carica solo quando serve davvero: a spunta spenta la tendina non c'è,
    // e senza permesso la chiamata tornerebbe 403.
    if (!puoScegliere || !richiede) return;
    anagraficaApi
      .listIstitutiVigilanza()
      .then(({ istituti: list }) => setIstituti(list))
      // L'elenco è un aiuto alla scelta: se non arriva resta "Altro", che è
      // sempre compilabile. Meglio di un form che non si può salvare.
      .catch(() => setIstituti([]));
  }, [puoScegliere, richiede]);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-line p-3">
      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-black/20"
          checked={richiede}
          disabled={disabled}
          onChange={(e) => onRichiedeChange(e.target.checked)}
        />
        Da effettuare congiuntamente alla vigilanza
      </label>

      {richiede && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Istituto di Vigilanza coinvolto</label>

          {puoScegliere ? (
            <select
              value={scelta}
              disabled={disabled}
              onChange={(e) => {
                onSceltaChange(e.target.value);
                // Cambiando istituto il testo "Altro" non deve sopravvivere:
                // resterebbe a nominare un istituto diverso da quello scelto.
                if (e.target.value !== ALTRO) onAltroChange("");
              }}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink disabled:opacity-60"
            >
              <option value="">— Non specificato —</option>
              {istituti.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nome}
                </option>
              ))}
              <option value={ALTRO}>Altro (non in elenco)</option>
            </select>
          ) : (
            <p className="text-sm text-ink">
              <span className="font-semibold">{congiuntoCon ?? "—"}</span>{" "}
              <span className="text-ink/50">(lo imposta chi gestisce l'anagrafica)</span>
            </p>
          )}

          {puoScegliere && scelta === ALTRO && (
            <Input
              label="Nome dell'istituto"
              value={altro}
              disabled={disabled}
              onChange={(e) => onAltroChange(e.target.value)}
              placeholder="Come si chiama l'istituto coinvolto"
            />
          )}

          <p className="text-xs text-ink/50">
            L'istituto indicato qui vede questo intervento nella propria lista. Non cambia a chi è assegnato:
            l'intervento resta del Centro Assistenza.
          </p>
        </div>
      )}
    </div>
  );
}
